import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import './primitives.css';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tint'
  | 'tint-outline'
  | 'dark'
  | 'inverse'
  | 'outline-on-dark'
  | 'plain';

export type ButtonSize = 'lg' | 'md' | 'sm' | 'xs';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
}

/** Local button primitive. Not @dedupr/ui's Button — see styles/tokens.css. */
export function Button({
  variant = 'secondary',
  size = 'md',
  className = '',
  children,
  ...rest
}: ButtonProps) {
  const cls = `land-btn land-btn--${variant} land-btn--${size} ${className}`.trim();
  return (
    <button className={cls} {...rest}>
      {children}
    </button>
  );
}

export interface LinkButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
}

/** Same visual as Button, rendered as an <a> for links to an external destination. */
export function LinkButton({
  variant = 'secondary',
  size = 'md',
  className = '',
  children,
  ...rest
}: LinkButtonProps) {
  const cls = `land-btn land-btn--${variant} land-btn--${size} ${className}`.trim();
  return (
    <a className={cls} {...rest}>
      {children}
    </a>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: number;
  children: ReactNode;
}

export function IconButton({ size = 36, className = '', style, children, ...rest }: IconButtonProps) {
  return (
    <button
      className={`land-icon-btn ${className}`.trim()}
      style={{ width: size, height: size, ...style }}
      {...rest}
    >
      {children}
    </button>
  );
}
